import { useApplicationIdContext } from '@n2-common/components/ApplicationContext';
import { type WizardStage } from '@n2-common/components/MultiStageOnePageWizard/types';
import { type FormikValues } from 'formik';
import React from 'react';
import { trackSectionView } from './gtm';
import { Tracking } from '@n2-common/services/o2Service';

export default function useTracking<T extends FormikValues>(wizardStep: number, stages: WizardStage<T>[], majorNumberUsedForTracking: string | null) {
    const { productType, country } = useApplicationIdContext(false, false);
    const lastWizardStepTracked = React.useRef<number>();
    React.useEffect(
        function tracking() {
            if (!stages || stages.length === 0) {
                return;
            }
            if (lastWizardStepTracked.current === wizardStep) {
                return;
            }
            if (majorNumberUsedForTracking === null) {
                return;
            }
            lastWizardStepTracked.current = wizardStep;
            const sectionName = stages[wizardStep]?.sectionNameForTracking;
            sectionName && trackSectionView(productType, country, `${majorNumberUsedForTracking}.${wizardStep}`, sectionName);
        },
        [wizardStep, productType, country, majorNumberUsedForTracking, stages, lastWizardStepTracked]
    );
}

// We need measurement_id to get session_id from gtag, but it is not available in GTM.
// We work around this by parsing the script tag that loads gtag.
// This is a bit hacky, but it is the only way to get the measurement_id.
function getMeasurementId() {
    const scripts = [].filter.call(document.getElementsByTagName('script'), (s) => 'src' in s && `${s['src']}`.includes('gtag/js'));
    const script = scripts[0];
    if (script && 'src' in script) {
        try {
            const { search } = new URL(script['src']);
            const qs = getSearchObject(search);
            return qs['id'];
        } catch (e) {
            console.error(e);
        }
    }
    return undefined;
}

function gtag(..._: unknown[]) {
    if ('dataLayer' in window && Array.isArray(window.dataLayer)) {
        // eslint-disable-next-line prefer-rest-params -- we need to pass arguments object to dataLayer.push, while internal Google implementation do some lookups / comparisons with callee.
        window.dataLayer.push(arguments);
    }
}

async function getGtagValue(target: string, fieldName: string): Promise<string> {
    //console.log('GA4', target, fieldName);
    return new Promise((resolve) => {
        gtag('get', target, fieldName, resolve);
    });
}

export async function getGaClientid() {
    const measurementId = getMeasurementId();
    if (measurementId) {
        return await getGtagValue(measurementId, 'client_id');
    }
    return null;
}

export async function getTracking() {
    const tracking: Tracking = {};
    try {
        const measurementId = getMeasurementId();
        if (measurementId) {
            const gaClientid = await getGtagValue(measurementId, 'client_id');
            if (gaClientid) {
                tracking.gaClientId = gaClientid;
            }
            const gaSessionId = await getGtagValue(measurementId, 'session_id');

            if (gaSessionId) {
                tracking.gaSessionId = gaSessionId;
            }
        }
    } catch (e) {
        console.error('Failed to get tracking', e);
    }

    const { search } = location;
    if (search) {
        return {
            ...tracking,
            ...getSearchObject(search),
        };
    }
    return tracking;
}

function getSearchObject(search: string): Record<string, string> {
    return Object.fromEntries(
        search
            .slice(1)
            .split('&')
            .map((kv) => kv.split('='))
    );
}
